import { Alert, Button, CircularProgress, Stack, Typography } from "@mui/material";
import React from "react";
import { forwardRef, Dispatch, SetStateAction, useEffect } from "react";
import { assetService } from "../../../../Core/Api/services";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import { GetGraphResponse } from "../../../../Core/Api/Types/get-graph-response";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { GetMetadataByTagName, IsContentShareable } from "../../../../Core/Utils/asset-object-utils";
import SelectableCard from "../../../Cards/SelectableCard";
import OutlinedCheckbox from "../../../Forms/OutlinedCheckbox";
import { ShareFeature, ShareFormValues } from "../modal-create-share";
import { useTranslation } from "react-i18next";

export type ModalCreateShareStepTwoElementProps = {
  formValues: ShareFormValues;
  setFormValues: Dispatch<SetStateAction<ShareFormValues>>;
};

const ModalCreateShareStepTwo = forwardRef<any, ModalCreateShareStepTwoElementProps>((props, ref) => {
  const { formValues, setFormValues } = props;
  const [isLoading, setIsLoading] = React.useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (!formValues.stepTwo.feature) {
      assetService.GetObjectById(formValues.stepOne?.objectId!).then(function (response: GetGraphResponse) {
        setFormValues((prevState) => ({
          ...prevState,
          stepOne: {
            ...prevState.stepOne,
            features: response.graph?.childObjects
              ?.filter(
                (x) =>
                  IsContentShareable(x) &&
                  (GetMetadataByTagName(x, "contentType")?.value === "Episode"
                    ? computeEpisodesFromParent(x).length > 0
                    : true)
              )
              .map((child: DbAssetObjectInfos) => {
                return {
                  id: child.id,
                  name: child.name,
                  episodes: [],
                  epList: computeEpisodesFromParent(child),
                };
              }),
          },
        }));
        setIsLoading(false);
      });
    } else setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!formValues.stepOne.features) return;

    if (!formValues.preselectedContentId) return;

    let feature = formValues.stepOne.features.find((x) => x.id === formValues.preselectedContentId);

    if (feature) onClickFeature(feature);
  }, [formValues.stepOne.features]);

  const computeEpisodesFromParent = (object: DbAssetObjectInfos) => {
    let epList: number[] = [];
    object.childObjects?.forEach((element) => {
      if (
        element.classification === ObjectClass.Mezz &&
        element.status === ObjectStatus.Accepted &&
        element.layer === ObjectLayer.Asset
      ) {
        let epNb: number = GetMetadataByTagName(element, "serieEpisodeNumberProduction")?.value;
        if (epNb && !epList.includes(epNb)) epList.push(epNb);
      }
    });

    return epList.sort(function (a, b) {
      return a - b;
    });
  };

  const onClickFeature = (value: ShareFeature) => {
    let isFormValid: boolean = false;
    if (value.epList.length === 0) isFormValid = true;

    setFormValues((prevState) => ({
      ...prevState,
      stepTwo: {
        feature: value,
        epSelected: [],
      },
      localizationSection: {
        ...prevState.localizationSection,
        audioLanguageOptions: [],
        subtitleLanguageOptions: [],
        localizationAudioSelected: [],
        localizationSubtitlesSelected: [],
      },
      isFormValid: isFormValid,
    }));
  };

  const onClickSubContent = (value: number) => {
    let epSelected: number[] = formValues.stepTwo.epSelected.includes(value)
      ? formValues.stepTwo.epSelected.filter((epNb) => epNb !== value)
      : formValues.stepTwo.epSelected.concat(value);
    let isFormValid: boolean = false;

    if (formValues.stepTwo.feature && epSelected.length > 0) isFormValid = true;

    setFormValues((prevState) => ({
      ...prevState,
      stepTwo: {
        ...prevState.stepTwo,
        epSelected: epSelected,
      },
      localizationSection: {
        ...prevState.localizationSection,
        audioLanguageOptions: [],
        subtitleLanguageOptions: [],
        localizationAudioSelected: [],
        localizationSubtitlesSelected: [],
      },
      isFormValid: isFormValid,
    }));
  };

  useEffect(() => {
    let validation: boolean = formValues.stepTwo.feature !== null;
    if (validation !== formValues.isFormValid)
      setFormValues((prevState) => ({
        ...prevState,
        isFormValid: validation,
      }));
  }, []);

  return (
    <>
      {isLoading && (
        <Stack alignItems={"center"}>
          <CircularProgress />
        </Stack>
      )}
      {!isLoading &&
        (formValues.stepOne.features && formValues.stepOne.features?.length > 0 ? (
          <Stack spacing={1}>
            <Typography variant="h5" fontWeight={600} mb={2}>
              {t("modal:modalCreateShareStep.subTitle.selectContentToShare")}
            </Typography>

            {formValues.stepOne.features?.map((feature: ShareFeature) => (
              <SelectableCard
                key={feature.id}
                onClick={() => onClickFeature(feature)}
                selected={feature.id === formValues.stepTwo.feature?.id}
              >
                {feature.name}
              </SelectableCard>
            ))}
          </Stack>
        ) : (
          <Alert severity="info">{t("alert:info.cannotShareTitle")}</Alert>
        ))}

      {!isLoading &&
        formValues.stepTwo.feature &&
        formValues.stepOne.isSeason &&
        formValues.stepTwo.feature.epList.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="h5" fontWeight={600} mt={2}>
              {t("modal:modalCreateShareStep.subTitle.selectEpisodes")}
            </Typography>
            <Stack spacing={1} direction="row">
              <Button
                onClick={() =>
                  setFormValues((prevState) => ({
                    ...prevState,
                    stepTwo: {
                      ...prevState.stepTwo,
                      epSelected: prevState.stepTwo.epSelected.length === 0 ? formValues.stepTwo.feature!.epList : [],
                    },
                    localizationSection: {
                      ...prevState.localizationSection,
                      audioLanguageOptions: [],
                      subtitleLanguageOptions: [],
                      localizationAudioSelected: [],
                      localizationSubtitlesSelected: [],
                    },
                    isFormValid: true,
                  }))
                }
              >
                {formValues.stepTwo.epSelected.length > 0 ? t("common:unselectAll") : t("common:selectAll")}
              </Button>
            </Stack>
            {formValues.stepTwo.feature.epList.map((ep: number) => (
              <OutlinedCheckbox
                key={ep}
                checked={formValues.stepTwo.epSelected.includes(ep)}
                onClick={() => onClickSubContent(ep)}
              >
                <Typography fontWeight={500}>
                  {t("modal:modalCreateShareStep.subTitle.episodeNumber", { ep })}
                </Typography>
              </OutlinedCheckbox>
            ))}
          </Stack>
        )}
    </>
  );
});

export default ModalCreateShareStepTwo;
